import { useState, useEffect, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {LayoutDashboard, Building2, Briefcase, Users, Star, AlertTriangle, Bell, LogOut, Menu, TrendingUp, UserCheck, X, ThumbsUp, ClipboardList, FileText, Archive, SlidersHorizontal} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications'

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/empresa/dashboard' },
  { icon: Building2, label: 'Perfil empresa', to: '/empresa/perfil' },
  { icon: Briefcase, label: 'Vacantes', to: '/empresa/vacantes' },
  { icon: UserCheck, label: 'Postulantes', to: '/empresa/postulantes' },
  { icon: Users, label: 'Colaboradores', to: '/empresa/colaboradores' },
  { icon: Star, label: 'Evaluaciones', to: '/empresa/evaluaciones' },
  { icon: SlidersHorizontal, label: 'Aspectos de evaluación', to: '/empresa/aspectos' },
  { icon: TrendingUp, label: 'Reportes', to: '/empresa/reportes' },
  { icon: ThumbsUp, label: 'Mi reputación', to: '/empresa/reputacion' },
  { icon: FileText, label: 'Plantillas', to: '/empresa/plantillas' },
  { icon: ClipboardList, label: 'Entrevistas', to: '/empresa/entrevistas' },
  { icon: Archive, label: 'Banco de talento', to: '/empresa/banco-talento' },
  { icon: AlertTriangle, label: 'Casos', to: '/empresa/casos' },
  { icon: Bell, label: 'Notificaciones', to: '/empresa/notificaciones' },
]

interface EmpresaLayoutProps {
  title: string
  subtitle?: string
  children: ReactNode
}

function EmpresaLayout({ title, subtitle, children }: EmpresaLayoutProps) {
  const { user } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)
  const { unread } = useUnreadNotifications('empresas', user?.userId)

  useEffect(() => {
    if (!user) return
    const loadPending = () => {
      lumi.entities.applications
        .list({ filter: { companyUserId: user.userId, status: 'pendiente' } })
        .then((r) => setPendingCount(r.list.length))
        .catch(() => {})
    }
    loadPending()
    const handler = () => loadPending()
    window.addEventListener('beeznz:applications-changed', handler)
    return () => window.removeEventListener('beeznz:applications-changed', handler)
  }, [user])

  const handleLogout = async () => {
    await lumi.auth.signOut()
    window.location.href = '/'
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f8fafc' }}>
      {/* Overlay móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col transition-transform md:static md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: '#0d1421', borderRight: '1px solid rgba(255,255,255,0.07)' }}
      >
        {/* Logo superior */}
        <div className="flex h-16 items-center justify-between px-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <Link to="/empresa/dashboard" onClick={() => setSidebarOpen(false)}>
            <img
              src="https://static.lumi.new/93/93deb8f4c6e571fa108d99af77ea2979.webp"
              alt="Beeznz Work"
              className="h-8 w-auto"
            />
          </Link>
          <button
            className="md:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-0.5 scrollbar-none" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {navItems.map(({ icon: Icon, label, to }) => {
            const active = location.pathname === to
            const showPending = to === '/empresa/postulantes' && pendingCount > 0
            const showNotif = to === '/empresa/notificaciones' && unread > 0
            const badgeValue = showPending ? pendingCount : showNotif ? unread : 0
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-white/8 hover:text-white'
                }`}
                style={active ? {} : {}}
              >
                <span className="relative">
                  <Icon size={17} className={active ? 'text-amber-400' : ''} />
                  {showNotif && (
                    <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-[#0d1421]" />
                  )}
                </span>
                <span className="flex-1">{label}</span>
                {(showPending || showNotif) && (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold leading-none text-white">
                    {badgeValue > 99 ? '99+' : badgeValue}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* Logo pie + Cerrar sesión */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="px-4 py-4 flex items-center justify-center">
            <img
              src="https://static.lumi.new/d5/d5398fcebcce92c05914f918c9cc339e.webp"
              alt="Powered by Beeznz"
              className="h-10 w-auto opacity-80"
            />
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-5 py-4 text-sm font-semibold transition-colors hover:bg-white/5"
              style={{ color: '#f87171' }}
            >
              <LogOut size={17} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Contenido principal */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Área de contenido */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <div className="mb-6 flex items-start gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden mt-1 rounded-lg p-1.5 text-gray-500 hover:text-gray-900 flex-shrink-0"
            >
              <Menu size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              {subtitle ? <p className="mt-1 text-sm text-gray-500">{subtitle}</p> : null}
            </div>
          </div>
          {children}
        </main>
      </div>
    </div>
  )
}

export default EmpresaLayout
